import type React from 'react';
import { Component, createRef, Fragment } from 'react';
import { ConfigContext } from '../context/ConfigContext';
import { isWelcomeMessageShown } from '../helpers/textInputVisibility';
import {
  type ChatBubbleInfo,
  type Message,
  type MessagePayload,
  Sender,
  type StreamingState,
} from '../types/ChatWindow';
import type { MessageScheme } from '../types/configs';
import Avatar from './Avatar';
import ButtonedImageMessage from './message/ButtonedImageMessage';
import ButtonedTextMessage from './message/ButtonedTextMessage';
import ImageCarousel from './message/carousel/ImageCarousel';
import { DropdownMessage } from './message/DropdownMessage';
import ImageMessage from './message/ImageMessage';
import { RatingMessage } from './message/RatingMessage';
import SystemMessage from './message/SystemMessage';
import StreamingBubble from './message/text/StreamingBubble';
import TextMessage from './message/text/TextMessage';
import TimeStamp from './TimeStamp';
import TypingIndicator from './TypingIndicator';

type MessagesProps = {
  messages: Array<Message>;
  submitHandler: (...args: any[]) => void;
  typingState: 'typing_on' | 'typing_off';
  streamingMessage?: StreamingState | null;
  onStreamComplete?: () => void;
};

class Messages extends Component<MessagesProps, { isTyping: boolean }> {
  static contextType = ConfigContext;
  context!: React.ContextType<typeof ConfigContext>;
  scrollContainer = createRef<HTMLDivElement>();

  constructor(props: MessagesProps) {
    super(props);
    const { typingState } = this.props;
    this.state = {
      isTyping: typingState === 'typing_on',
    };
  }

  componentDidMount() {
    const { messages } = this.props;
    // If there are messages stored then scroll to the bottom
    if (messages.length > 0) {
      this.scrollToBottom();
    }
  }

  componentDidUpdate(prevProps: MessagesProps) {
    const { messages, typingState, streamingMessage } = this.props;
    // Comparing props to update
    if (messages !== prevProps.messages) {
      // Scroll to bottom
      this.scrollToBottom();
      if (typingState === prevProps.typingState) {
        this.setTyping(prevProps.typingState);
      }
    }
    if (typingState !== prevProps.typingState) {
      this.setTyping(typingState);
    }
    if (typingState) {
      this.scrollToBottom();
    }
    // * Scroll as streaming chunks arrive so the user sees the latest text.
    if (streamingMessage !== prevProps.streamingMessage && streamingMessage) {
      this.scrollToBottom();
    }
  }

  setTyping(action: 'typing_on' | 'typing_off') {
    const isTyping = action === 'typing_on';
    this.setState({
      isTyping,
    });
  }

  scrollToBottom = () => {
    if (this.scrollContainer.current) {
      requestAnimationFrame(() => {
        if (this.scrollContainer.current) {
          const { scrollHeight, clientHeight } = this.scrollContainer.current;
          this.scrollContainer.current.scrollTop = scrollHeight - clientHeight;
        }
      });
    }
  };

  // Returns true for the authoritative final message of a streaming turn.
  // These messages should not re-animate - the streaming bubble already bounced in.
  isStreamingFinalMessage = (message: Message): boolean => {
    const payload = message.payload;
    if (payload.type !== 'text' && payload.type !== 'textWithButtons') {
      return false;
    }
    return (
      Boolean(payload.options?.streamId) && Boolean(payload.options?.final)
    );
  };

  useStyle = (message: Message, style: MessageScheme) => {
    const styles = {};
    const messageType = message?.payload?.type;

    // * For both text and textWithButtons set the styles for urls and collapsible sections
    if (['text', 'textWithButtons'].includes(messageType)) {
      Object.assign(styles, {
        '--url-color': style.urlColor,
        '--url-hover-color': style.urlHoverColor,
        '--url-click-color': style.urlClickColor,
        '--url-text-decoration': style.urlTextDecoration,
        '--message-bubble-color': style.messageBubbleColor,
        '--message-bubble-text-color': style.messageBubbleTextColor,
      });
    }

    // * Set colors only for text blocks
    if (messageType === 'text') {
      Object.assign(styles, {
        color: style.messageBubbleTextColor,
        backgroundColor: style.messageBubbleColor,
      });
    }

    return styles;
  };

  /**
   * returns the style of the message in case of text and textwithbuttons
   * @param message user conversations messages
   * @param messageScheme user defined config scheme
   */
  prepareStyle = (
    message: Message,
    messageScheme: MessageScheme,
  ): MessageScheme => {
    const {
      personalMessageBubbleColor,
      personalMessageBubbleTextColor,
      messageBubbleColor,
      messageBubbleTextColor,
    } = messageScheme;
    const bubbleColor =
      message.direction === 'out'
        ? messageBubbleColor
        : personalMessageBubbleColor;
    const bubbleTextColor =
      message.direction === 'out'
        ? messageBubbleTextColor
        : personalMessageBubbleTextColor;
    return {
      ...messageScheme,
      messageBubbleColor: bubbleColor,
      messageBubbleTextColor: bubbleTextColor,
    };
  };

  /**
   * Chuck messages, turn [] into [[]], with a new inner array for every direction switch
   * @param messages user conversations messages
   */
  chunkMessageStructure = (messages: Message[]) => {
    const messageSets: Message[][] = [];
    let messagesHelper: Message[] = [];
    let currentDirection = messages[0].direction;
    messages.forEach((currentMessage) => {
      if (currentDirection !== currentMessage.direction) {
        messageSets.push(messagesHelper);
        messagesHelper = [];
      }
      messagesHelper.push(currentMessage);
      currentDirection = currentMessage.direction;
    });

    messageSets.push(messagesHelper);
    return messageSets;
  };

  getChatBubbleInfo = (
    message: Message,
    defaultAvatarImg: string,
  ): ChatBubbleInfo => {
    if (
      'options' in message.payload &&
      message?.payload?.options &&
      'agentData' in message.payload.options &&
      message.payload.options.agentData
    ) {
      const { firstName, profilePic } = message.payload.options.agentData;

      return {
        agentName: (firstName ?? '').trim(),
        avatarImg: profilePic ?? defaultAvatarImg,
      };
    }

    return { agentName: '', avatarImg: defaultAvatarImg };
  };

  getMessageStructure = (
    messages: Message[],
    messageScheme: MessageScheme & { messageIcon: string },
    notificationAIGeneratedAnswer: string,
  ) => {
    const { messageIcon, notificationAIGeneratedAnswerTextColor } =
      messageScheme;
    if (messages && messages.length > 0) {
      const setOfMessages = this.chunkMessageStructure(messages);

      return setOfMessages.map((messageSet, indexSet) => {
        return (
          <div className='message-container' key={`${indexSet}-set`}>
            {messageSet.map((message, index) => {
              const style = this.prepareStyle(message, messageScheme);
              const isLastMessage =
                setOfMessages.length === indexSet + 1 &&
                messageSet.length === index + 1;

              const { agentName, avatarImg } = this.getChatBubbleInfo(
                message,
                messageIcon,
              );

              const isAIMessage =
                'options' in message.payload &&
                message.payload?.options &&
                'sentBy' in message.payload.options &&
                message.payload.options?.sentBy === Sender.AI;

              return (
                <Fragment key={`${indexSet}-${index}`}>
                  {message.direction === 'system' ? (
                    <div className='system-message-direction'>
                      {this.renderSwitch(message.payload, style, isLastMessage)}
                    </div>
                  ) : (
                    <>
                      {agentName && (
                        <div className='agent-name'>{agentName}</div>
                      )}
                      <div
                        className={`message-row ${message.direction === 'in' ? 'direction-in' : 'direction-out'}`}>
                        <div
                          className={`message-content ${message?.payload?.type ?? ''} ${message.direction === 'in' ? 'direction-in' : 'direction-out'}${this.isStreamingFinalMessage(message) ? ' animation-none' : ''}`}
                          // biome-ignore lint/correctness/useHookAtTopLevel: is not a hook
                          style={this.useStyle(message, style)}>
                          {message.direction === 'out' ? (
                            <Avatar avatarImg={avatarImg} />
                          ) : null}
                          {this.renderSwitch(
                            message.payload,
                            style,
                            isLastMessage,
                          )}
                        </div>
                        <div
                          className={`message-footer ${message.direction === 'in' ? 'direction-in' : 'direction-out'}`}>
                          {messages.length === index + 1 ? <TimeStamp /> : null}
                          {isAIMessage ? (
                            <span
                              className='notification-ai-generated-answer'
                              style={{
                                color: notificationAIGeneratedAnswerTextColor,
                              }}>
                              {notificationAIGeneratedAnswer}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </>
                  )}
                </Fragment>
              );
            })}
          </div>
        );
      });
    }
    return null;
  };

  renderSwitch(
    message: MessagePayload,
    scheme: MessageScheme,
    isLastMessage: boolean,
  ) {
    const { submitHandler } = this.props;

    switch (message.type) {
      case 'text':
        return <TextMessage data={message.data} />;
      case 'image':
        return <ImageMessage data={message.data} />;
      case 'textWithButtons':
        return (
          <ButtonedTextMessage
            data={message.data}
            scheme={scheme}
            submitHandler={submitHandler}
            isLastMessage={isLastMessage}
            suppressAnimation={
              Boolean(message.options?.streamId) &&
              Boolean(message.options?.final)
            }
          />
        );
      case 'imageWithButtons': // Deprecated, it is not used anymore
        return (
          <ButtonedImageMessage
            data={message.data}
            scheme={scheme}
            submitHandler={submitHandler}
            isLastMessage={isLastMessage}
          />
        );
      case 'carousel':
        return (
          <ImageCarousel
            data={message.data}
            options={message?.options}
            scheme={scheme}
            submitHandler={submitHandler}
          />
        );
      case 'feedback':
        return (
          <RatingMessage
            scheme={scheme}
            submitHandler={submitHandler}
            data={message.data}
            isLastMessage={isLastMessage}
          />
        );
      case 'dropdown':
        return (
          <DropdownMessage
            scheme={scheme}
            submitHandler={submitHandler}
            data={message.data}
            isLastMessage={isLastMessage}
          />
        );
      case 'system': {
        const {
          scheme: {
            header: { windowColor, headerColor },
          },
        } = this.context;

        return (
          <SystemMessage
            data={message.data}
            textColor={headerColor}
            windowColor={windowColor}
          />
        );
      }
      default:
        return null;
    }
  }

  render() {
    const { messages, streamingMessage, onStreamComplete } = this.props;
    const { isTyping } = this.state;
    const {
      scheme: {
        header: { windowColor },
        message: {
          messageBubbleColor,
          messageBubbleTextColor,
          streamingIndicatorColor,
        },
        message: messageScheme,
      },
      messageIcon,
      welcomeMessage,
      showWelcomeAsFirstMessage,
      notificationAIGeneratedAnswer,
      useStreamingResponse,
    } = this.context;

    return (
      <div
        className='messages-container'
        id='messages'
        style={{ backgroundColor: windowColor }}>
        <div
          ref={this.scrollContainer}
          className='messages-scrollable-container'>
          {isWelcomeMessageShown({
            showWelcomeAsFirstMessage,
            welcomeMessage,
          }) && (
            <div className='message-container'>
              <div className='message-row direction-out'>
                <div
                  className='message-content welcome'
                  style={{
                    backgroundColor: messageBubbleColor,
                    color: messageBubbleTextColor,
                  }}>
                  {welcomeMessage}
                </div>
                <div className='message-footer direction-out'>
                  {messages.length === 0 ? <TimeStamp /> : null}
                </div>
              </div>
            </div>
          )}
          {messages
            ? this.getMessageStructure(
                messages,
                {
                  ...messageScheme,
                  messageIcon,
                },
                notificationAIGeneratedAnswer,
              )
            : null}
          {streamingMessage && (
            <StreamingBubble
              streamingMessage={streamingMessage}
              messageBubbleColor={messageBubbleColor}
              messageBubbleTextColor={messageBubbleTextColor}
              streamingIndicatorColor={streamingIndicatorColor}
              avatarImg={messageIcon}
              onComplete={onStreamComplete}
              onProgress={this.scrollToBottom}
            />
          )}
          {/* Suppress the regular typing indicator while the streaming bubble is visible to avoid two dot animations at once. */}
          <TypingIndicator
            show={isTyping && !streamingMessage}
            indicatorColor={
              useStreamingResponse ? streamingIndicatorColor : undefined
            }
          />
        </div>
      </div>
    );
  }
}

export default Messages;
